import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "../../lib/apiBase";
import {
  Search, X, Mail as MailIcon, Eye, MousePointerClick, AlertTriangle,
  CheckCircle2, Send, Loader2, Clock, User as UserIcon,
} from "lucide-react";

const API = `${BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const STATUS_LABEL = {
  sent: { label: "gesendet", color: "bg-violet-100 text-violet-800" },
  scheduled: { label: "geplant", color: "bg-amber-100 text-amber-800" },
  skipped: { label: "übersprungen", color: "bg-slate-100 text-slate-700" },
  skipped_via_approve: { label: "via Freischaltung", color: "bg-slate-100 text-slate-700" },
  failed: { label: "fehlgeschlagen", color: "bg-rose-100 text-rose-700" },
};

const KIND_BADGE = {
  Lead: "bg-sky-100 text-sky-800",
  Kontakt: "bg-emerald-100 text-emerald-800",
  Kunde: "bg-violet-100 text-violet-800",
  Partner: "bg-amber-100 text-amber-800",
};

export default function MailHistoryLookup({ open, onClose, token }) {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  if (!open) return null;

  // Debounced typeahead — fires the /admin/contacts/search endpoint
  // 200ms after the user stops typing. Resets when input is empty or matches
  // a recently picked suggestion.
  const runSuggest = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setSuggestions([]); setSuggestOpen(false); return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data: res } = await axios.get(`${API}/admin/contacts/search`, {
          params: { q: q.trim(), limit: 8 },
          headers: { "X-Admin-Token": token },
        });
        setSuggestions(res.results || []);
        setSuggestOpen((res.results || []).length > 0);
        setHighlight(-1);
      } catch {
        setSuggestions([]); setSuggestOpen(false);
      }
    }, 200);
  };

  const handleQueryChange = (v) => {
    setQuery(v);
    runSuggest(v);
  };

  const pickSuggestion = (s) => {
    setQuery(s.email);
    setSuggestions([]); setSuggestOpen(false); setHighlight(-1);
    // Auto-run history lookup right after pick.
    runHistory(s.email);
  };

  const runHistory = async (rawEmail) => {
    setErr("");
    const key = String(rawEmail || "").trim().toLowerCase();
    if (!key || !key.includes("@")) {
      setErr("Bitte E-Mail aus den Vorschlägen wählen — oder vollständige E-Mail-Adresse eingeben.");
      return;
    }
    setBusy(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/mail-history`, {
        params: { email: key },
        headers: { "X-Admin-Token": token },
      });
      setData(res);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Konnte Mail-History nicht laden.");
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e?.preventDefault?.();
    // If suggestions are open and one is highlighted, use it.
    if (suggestOpen && highlight >= 0 && suggestions[highlight]) {
      pickSuggestion(suggestions[highlight]); return;
    }
    runHistory(query);
  };

  const onKeyDown = (e) => {
    if (!suggestOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => Math.max(0, i - 1));
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
    }
  };

  const resetAndClose = () => {
    setQuery(""); setData(null); setErr("");
    setSuggestions([]); setSuggestOpen(false); setHighlight(-1);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 pt-16"
      onClick={resetAndClose}
      data-testid="mail-history-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col overflow-visible"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-violet-100 flex items-center justify-center">
              <MailIcon className="h-5 w-5 text-[var(--al-purple)]" />
            </div>
            <div>
              <div className="font-display font-bold text-base">Mail-History</div>
              <div className="text-xs text-slate-500">Alle Mails an eine Adresse, mit Open/Click-Tracking.</div>
            </div>
          </div>
          <button
            onClick={resetAndClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            data-testid="mail-history-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-4 border-b border-slate-100">
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                onKeyDown={onKeyDown}
                onFocus={() => setSuggestOpen(suggestions.length > 0)}
                onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                placeholder="Name oder E-Mail eingeben (ab 2 Buchstaben Vorschläge)…"
                autoFocus
                autoComplete="off"
                data-testid="mail-history-input"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
              {suggestOpen && suggestions.length > 0 && (
                <div
                  data-testid="mail-history-suggestions"
                  className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-y-auto z-20"
                >
                  {suggestions.map((s, i) => (
                    <button
                      type="button"
                      key={`${s.collection}-${s.id || s.email}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickSuggestion(s)}
                      data-testid={`mail-history-suggestion-${i}`}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-violet-50 transition-colors border-b border-slate-50 last:border-0 ${i === highlight ? "bg-violet-50" : ""}`}
                    >
                      <UserIcon className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-display font-semibold text-slate-900 truncate">
                          {s.name || <span className="italic text-slate-400">ohne Name</span>}
                        </div>
                        <div className="text-xs text-slate-500 font-mono truncate">{s.email}</div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold ${KIND_BADGE[s.kind] || "bg-slate-100 text-slate-700"}`}>
                        {s.kind}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="mail-history-submit"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--al-purple)] hover:bg-violet-700 text-white text-sm font-display font-bold disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Suchen
            </button>
          </div>
        </form>

        {err && (
          <div className="px-6 py-3 bg-rose-50 border-b border-rose-200 text-sm text-rose-800 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {err}
          </div>
        )}

        {data && (
          <div className="flex-1 overflow-y-auto">
            {/* Totals */}
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">
                Übersicht — {data.email}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                <Stat icon={Send} label="Gesendet" value={data.totals.sent} color="text-violet-700 bg-violet-50" />
                <Stat icon={CheckCircle2} label="Zugestellt" value={data.totals.delivered} color="text-sky-700 bg-sky-50" />
                <Stat icon={Eye} label="Geöffnet" value={data.totals.opened} color="text-emerald-700 bg-emerald-50" />
                <Stat icon={MousePointerClick} label="Geklickt" value={data.totals.clicked} color="text-fuchsia-700 bg-fuchsia-50" />
                <Stat icon={AlertTriangle} label="Bounced" value={data.totals.bounced} color="text-rose-700 bg-rose-50" />
                <Stat icon={Clock} label="Übersprungen" value={data.totals.skipped} color="text-slate-700 bg-slate-50" />
              </div>
            </div>

            {/* Entries */}
            <div className="px-6 py-4 space-y-2">
              {data.entries.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  Keine Mails an diese Adresse versendet.
                </div>
              ) : (
                data.entries.map((e, idx) => (
                  <div
                    key={`${e.mail_id}-${e.sent_at || idx}`}
                    data-testid={`mail-history-row-${idx}`}
                    className="p-3 rounded-xl border border-slate-100 hover:border-violet-200 hover:bg-violet-50/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[11px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-bold">
                            {e.mail_id}
                          </span>
                          {(STATUS_LABEL[e.status] ? STATUS_LABEL[e.status] : { label: e.status, color: "bg-slate-100 text-slate-700" }).label && (
                            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold ${(STATUS_LABEL[e.status] || { color: "bg-slate-100 text-slate-700" }).color}`}>
                              {(STATUS_LABEL[e.status] || { label: e.status }).label}
                            </span>
                          )}
                          <span className="text-[10px] uppercase tracking-widest text-slate-400">
                            in <span className="font-mono text-slate-500">{e._collection}</span>
                          </span>
                        </div>
                        <div className="mt-1 text-sm font-display font-semibold text-slate-900 truncate">
                          {e.subject || "(kein Betreff)"}
                        </div>
                      </div>
                    </div>

                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      <Event icon={Send} label="Gesendet" iso={e.sent_at} color="text-violet-700" />
                      <Event icon={CheckCircle2} label="Zugestellt" iso={e.delivered_at} color="text-sky-700" />
                      <Event icon={Eye} label="Geöffnet" iso={e.opened_at} color="text-emerald-700" />
                      <Event icon={MousePointerClick} label="Geklickt" iso={e.clicked_at} color="text-fuchsia-700" />
                    </div>
                    {Array.isArray(e.clicks) && e.clicks.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 flex items-center gap-1">
                          <MousePointerClick className="h-3 w-3 text-fuchsia-600" /> Klick-Pfad ({e.clicks.length})
                        </div>
                        <ul className="space-y-1">
                          {e.clicks.map((c, ci) => (
                            <li key={ci} className="flex items-center gap-2 text-xs">
                              <span className="font-mono-tab text-[10px] text-slate-400 tabular-nums shrink-0">{fmt(c.at)}</span>
                              <a
                                href={c.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-fuchsia-700 hover:text-fuchsia-900 hover:underline truncate"
                                title={c.url}
                              >
                                {c.url}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {e.bounced_at && (
                      <div className="mt-1 text-xs text-rose-700 flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" /> Bounced: {fmt(e.bounced_at)}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className={`rounded-lg px-2.5 py-2 ${color}`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold opacity-80">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="text-xl font-mono-tab font-black mt-0.5">{value}</div>
  </div>
);

const Event = ({ icon: Icon, label, iso, color }) => (
  <div className={`flex items-center gap-1.5 ${iso ? color : "text-slate-300"}`}>
    <Icon className="h-3.5 w-3.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-bold leading-tight">{label}</div>
      <div className="text-xs font-mono leading-tight truncate">{iso ? fmt(iso) : "—"}</div>
    </div>
  </div>
);
